import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button, TextField, MenuItem, InputAdornment } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import client from '../Contentful_API/api';
import { PageHero, Section, SectionHeading, LoadingState, EmptyState } from '../Common/PageLayout';
import Pager from '../Common/Pager';
import TenderTable from './TenderTable';
import { tenderQuery } from './tenderUtils';
import './TenderCollection.css';

export default function TenderCollection({ archive = false }) {
    const [term, setTerm] = useState('');
    const [sort, setSort] = useState(archive ? '-fields.deadline' : 'fields.deadline');
    const [page, setPage] = useState(1);
    const [attempt, setAttempt] = useState(0);
    const [cutoff] = useState(() => new Date().toISOString());
    const [result, setResult] = useState({ items: [], total: 0, loading: true, error: false });
    useEffect(() => {
        let active = true;
        setResult(previous => ({ ...previous, loading: true, error: false }));
        const timer = setTimeout(() => {
            client.getEntries(tenderQuery({ archive, page, term, sort, cutoff }))
                .then(response => {
                    if (!active) return;
                    if (!Number.isInteger(response.total)) throw new Error('Missing tender count');
                    setResult({ items: response.items, total: response.total, loading: false, error: false });
                })
                .catch(() => {
                    if (active) setResult({ items: [], total: 0, loading: false, error: true });
                });
        }, 250);
        return () => { active = false; clearTimeout(timer); };
    }, [archive, page, term, sort, cutoff, attempt]);
    const title = archive ? 'Tender archive' : 'Open tenders';
    const totalPages = Math.max(1, Math.ceil(result.total / 12));
    return <>
        <Helmet><title>{title} | Ministry of Finance, Timor-Leste</title></Helmet>
        <PageHero eyebrow="Public procurement" title={title}
            description={archive ? 'A public record of past procurement notices, preserved for transparency and reference.' : 'Explore procurement opportunities and find the information you need to prepare your submission.'}
            breadcrumbs={archive ? [{ label: 'Tenders', to: '/tenders' }, { label: 'Archive' }] : [{ label: 'Tenders' }]}
            actions={!archive && <Button variant="contained" href="http://www.eprocurement.gov.tl/" target="_blank" rel="noopener noreferrer" endIcon={<OpenInNewIcon />} style={{ backgroundColor: 'white', color: '#143a66' }}>e-Procurement Portal</Button>} />
        <Section>
            <nav className="tender-collection-nav" aria-label="Tender collections">
                <Link to="/tenders" aria-current={!archive ? 'page' : undefined}>Open tenders</Link>
                <Link to="/tenders_archive" aria-current={archive ? 'page' : undefined}>Tender archive</Link>
            </nav>
            <SectionHeading overline={archive ? 'Procurement records' : 'Current notices'} title={archive ? 'Past tender notices' : 'Tenders open for submission'}
                description={archive ? 'Search closed notices by title, reference number or institution.' : 'Review the full notice for eligibility, documents and submission instructions. Closing times are shown in Timor-Leste time.'} />
            <div className="tender-toolbar">
                <TextField className="tender-search" id="tender-collection-search" label="Search notices" placeholder="Title, reference or institution" variant="outlined" size="small" value={term}
                    onChange={event => { setTerm(event.target.value); setPage(1); }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
                <TextField select id="tender-sort" label="Sort by" variant="outlined" size="small" value={sort} onChange={event => { setSort(event.target.value); setPage(1); }}>
                    <MenuItem value="fields.deadline">Closing date: earliest first</MenuItem>
                    <MenuItem value="-fields.deadline">Closing date: latest first</MenuItem>
                    <MenuItem value="-sys.createdAt">Recently added notices</MenuItem>
                </TextField>
                {term && <Button onClick={() => { setTerm(''); setPage(1); }}>Clear search</Button>}
            </div>
            {result.loading && <LoadingState label={archive ? 'Loading tender archive' : 'Loading open tenders'} />}
            {!result.loading && result.error && <div className="tender-error" role="alert"><strong>Tender notices could not be loaded.</strong><p>Please try again. This does not mean there are no tenders available.</p><Button variant="outlined" onClick={() => setAttempt(value => value + 1)}>Try again</Button></div>}
            {!result.loading && !result.error && <>
                <p className="tender-results" role="status">{result.total.toLocaleString()} {archive ? 'archived' : 'open'} notice{result.total === 1 ? '' : 's'}{result.total > 0 && ` · Showing ${(page - 1) * 12 + 1}–${Math.min(page * 12, result.total)}`}</p>
                {result.items.length ? <><TenderTable items={result.items} /><Pager page={page} totalPages={totalPages} onChange={setPage} /></> : <EmptyState
                    title={term ? 'No notices match your search' : archive ? 'No archived notices available' : 'No tenders currently open'}
                    description={term ? 'Try a different title, reference number or institution.' : archive ? 'Past notices will appear here after their closing deadline.' : 'Please check back for new notices, or browse the tender archive.'}
                    action={!archive && !term ? { label: 'Browse tender archive', to: '/tenders_archive' } : undefined} />}
            </>}
        </Section>
    </>;
}
